import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { AxiosRequestConfig } from 'axios';
import { SweetAlertResult } from 'sweetalert2';
import environment from 'environments';
import apiService from 'app/bff/services/apiService';
import { AaGetcaptchaResp } from 'app/bff/models/aa/aaGetcaptcha';
import { MbSigninResp } from 'app/bff/models/mb/mbSignin';
import { UserIdentityTypesEnum } from 'app/bff/enums/user';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { MbUpdateinfoResp } from 'app/bff/models/mb/mbUpdateinfo';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/Member/Forget/types';
import { RootState } from 'app/store/types';
import { sagaBoundary } from 'app/store/service';
import { ForgetState, MEMBER_FORGET__RESEND_OTP, MEMBER_FORGET__FETCH_CAPTCHA, MEMBER_FORGET__CHECK_CAPTCHA, MEMBER_FORGET__SEND_OTP, MEMBER_FORGET__AUTH_OTP, MEMBER_FORGET__SETUP_NEW_PASS_PHRASE, CheckCaptchaAction, SendOTPAction, AuthOTPAction, SetupNewPassPhraseAction } from './types';
import { resendOTPDoneAction, fetchCaptchaDoneAction, saveAuthPageDataAction, sendOTPAction, sendOTPDoneAction, setAccessibleStepAction, saveTemporaryAuthorizationAction } from './actions';
import commonService from 'app/core/services/commonService';
import { AaOtpSendcodeResp, AaOtpSendcodeReq } from 'app/bff/models/aa/aaOtpSendcode';
import { OTPActionsEnum } from 'app/bff/enums/otp';

/**
 * @description 取得圖形驗證碼
 */
function * fetchCaptcha () {
  const response: AaGetcaptchaResp = yield call(apiService.postAaGetcaptcha, {
    unsecure: null
  });
  if (response) yield put(fetchCaptchaDoneAction(response));
}

/**
 * @description 驗證圖形驗證碼
 */
function * checkCaptcha (action: CheckCaptchaAction) {
  const response: MbSigninResp = yield call(apiService.postMbSignin, {
    captcha: action.payload.checkCaptchaArgs,
    data: {
      actionType: 'OTP',
      action: '1',
      birthday: commonService.convertToADDate(action.payload.data.birthday),
      memberId: action.payload.data.id
    }
  });
  if (response.userType === UserIdentityTypesEnum.BH || response.userType === UserIdentityTypesEnum.OIS) {
    // 緩存頁面資料
    yield put(saveAuthPageDataAction(action.payload.data));
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 發送 OTP 驗證碼
    yield put(sendOTPAction(action.payload.sendOTPArgs, ROUTES.MEMBER__FORGET__AUTH__OTP_AUTH));
  }
}

/**
 * @description 發送 OTP 驗證碼
 */
function * sendOTP (action: SendOTPAction) {
  if (action.payload.redirectRoute) {
    // 若有轉導路由則執行導頁
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[action.payload.redirectRoute]));
    yield put(push(action.payload.redirectRoute));
  }
}

/**
 * @description 驗證 OTP 驗證碼
 */
function * authOTP (action: AuthOTPAction) {
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.args);
  if (response) {
    // 緩存暫時性「會員主檔」和「Access Token」
    yield put(saveTemporaryAuthorizationAction(response));
    // 將「設定新密碼」(Step-2) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.MEMBER__FORGET__SETUP]));
    // 導頁至「設定新密碼」(Step-2)
    yield put(push(ROUTES.MEMBER__FORGET__SETUP));
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP () {
  const forgetState: ForgetState = yield select((state: RootState) => state.features.member.forget);
  const request: AaOtpSendcodeReq = {
    action: OTPActionsEnum.FrontendLogin,
    emailAddr: forgetState.process.auth.otpAuth.otp?.emailAddr,
    mobile: forgetState.process.auth.otpAuth.otp?.mobile,
    memberId: forgetState.process.auth.data?.id ?? ''
  };
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, request);
  if (response && forgetState.process.auth.otpAuth) {
    // 更新 OTP 資料
    yield put(resendOTPDoneAction({
      ...forgetState.process.auth.otpAuth.otp,
      userType: forgetState.process.auth.otpAuth.otp?.userType ?? '',
      emailAddr: response.emailAddr,
      mobile: response.mobile,
      sn: response.sn,
      expireTime: response.expireTime
    }));
  }
}

/**
 * @description 設置會員新密碼
 */
function * setupNewPassPhrase (action: SetupNewPassPhraseAction) {
  const temporaryAuthorizationState: ForgetState['static']['temporaryAuthorization'] = yield select((state: RootState) => state.features.member.forget.static.temporaryAuthorization);
  if (!temporaryAuthorizationState) return;
  const requestConfig: AxiosRequestConfig = { headers: { Token: `${environment.backend.api.headers.authorization.prefix} ${temporaryAuthorizationState.token}` } };
  const response: MbUpdateinfoResp = yield call(apiService.postMbUpdateinfo, action.payload.args, requestConfig);
  if (response) {
    const result: SweetAlertResult = yield modalService.confirm('重設成功', '新密碼設定完成，請利用新密碼登入', '立即登入', '回首頁');
    if (result.value) {
      // 開啟會員登入 Modal
      modalService.login(true);
    }
    yield put(push(ROUTES.HOME__MAIN));
  }
}

export default function * watchForgetSaga () {
  yield all([
    takeEvery(MEMBER_FORGET__FETCH_CAPTCHA, sagaBoundary(fetchCaptcha)),
    takeEvery(MEMBER_FORGET__CHECK_CAPTCHA, sagaBoundary(checkCaptcha)),
    takeEvery(MEMBER_FORGET__SEND_OTP, sagaBoundary(sendOTP)),
    takeEvery(MEMBER_FORGET__AUTH_OTP, sagaBoundary(authOTP)),
    takeEvery(MEMBER_FORGET__SETUP_NEW_PASS_PHRASE, sagaBoundary(setupNewPassPhrase)),
    takeEvery(MEMBER_FORGET__RESEND_OTP, sagaBoundary(resendOTP))
  ]);
}
